/** Canonical set of action labels that can appear in the FormActionBar.
 *  Both the builder checklist and the action bar filter use this list.
 *
 *  **These strings are DATA.** Each one is persisted per form definition in
 *  `FormDefinition.allowedActions` and compared by value, so renaming an entry
 *  here would silently switch off that action on every form already configured.
 *  To change what an action is CALLED, add a display override below. */
/**
 * The action that fronts the product menu, by its PERSISTED value.
 *
 * Referenced through this constant rather than by literal, because three things
 * key off it — whether the "P" icon renders, what its menu is titled, and
 * whether Complete Form asks about an uncreated product — and a fourth reader
 * spelling it by hand is how one of them gets missed.
 */
export const PRODUCT_ACTION = 'Create Product'

/** What `PRODUCT_ACTION` is called wherever a person reads it. */
export const PRODUCT_ACTION_LABEL = 'Product Actions'

/**
 * The action that makes a step run more than once, by its PERSISTED value.
 *
 * STRUCTURAL, unlike every other entry in the list below: it shapes the
 * procedure-template sequence rather than putting a button on a case's action
 * bar. `FormActionBar` renders nothing for it — the bar filters against its own
 * `ACTIONS` array, which this is deliberately not in, exactly as
 * `PRODUCT_ACTION` is handled.
 *
 * Mirrored on the server as `REPEAT_ACTION` in
 * `cgt-backend/src/procedure-templates/repeats.ts`. These strings are DATA,
 * compared by value on both sides of the wire — change one and you must change
 * the other.
 */
export const REPEAT_ACTION = 'Repeat Form'

/**
 * Lets a form be skipped in its run from the "+" menu, with a reason on the
 * audit trail (z8nrz7fn5r). Unskip is offered on a skipped form whether or not
 * the action is still configured, so a form is never stuck skipped.
 */
export const SKIP_ACTION = 'Skip Form'

/**
 * Raise a follow-up on the form being filled in (z8nrz7fkfh). Configuring it
 * also puts the Action (n) badge, the form's open action items, on its bar.
 */
export const ACTION_ITEMS_ACTION = 'Action Items'

/**
 * Laboratory results typed on the form by hand and signed onto one product
 * (z8nrz7g2wm): for when there is no LIS automation or it is down. Configured
 * on its own, beside Order Test Results, which asks the LIS instead.
 */
export const APPLY_TEST_RESULTS_ACTION = 'Apply Test Results'

export const FORM_ACTION_LABELS: string[] = [
  'Add Form',
  'Reserve Equipment',
  'Reserve Inventory',
  'Reserve Space',
  'Create CAPA',
  'Create Product',
  'Order Test Results',
  APPLY_TEST_RESULTS_ACTION,
  'Display Patient History',
  REPEAT_ACTION,
  SKIP_ACTION,
  ACTION_ITEMS_ACTION,
]

/**
 * Display-only renames: persisted value → what a user reads.
 *
 * `Create Product` fronts a MENU of product operations (create, split, pool,
 * receive, store), so neither "Create" nor "Update" describes it — it reads as
 * "Product Actions" everywhere it is shown, including the title of the menu the
 * "P" icon opens. The stored value stays `Create Product`, so no form definition
 * needs migrating and every configured form keeps working.
 *
 * Add to this map rather than editing the array above.
 */
export const FORM_ACTION_DISPLAY_LABELS: Record<string, string> = {
  'Create Product': PRODUCT_ACTION_LABEL,
}

/** What to show for a persisted action value. Unmapped values read as themselves. */
export function formActionLabel(value: string): string {
  return FORM_ACTION_DISPLAY_LABELS[value] ?? value
}

/**
 * The action checklist, ready to render: `value` is what to store and compare,
 * `label` is what to show.
 *
 * Every checklist should map over THIS rather than over `FORM_ACTION_LABELS`.
 * There are three of them — the Forms list, the form editor's settings panel and
 * FormDefinitionPanel — and a display rename applied by hand missed two of them
 * on the first pass. Iterating a pair makes showing the right name the default
 * and printing the raw value the conspicuous mistake.
 *
 * **Sorted by LABEL, not by value** — alphabetical in the reading, which is the
 * only order a person scanning the checklist can verify. Sorting by the stored
 * value would file `Create Product` under C while it reads "Product Actions",
 * putting it between `Create CAPA` and `Display Patient History` for no reason
 * a user could see.
 *
 * Sorted here rather than by hand-ordering `FORM_ACTION_LABELS`, so the list
 * cannot drift out of order the next time an action is appended — and so that
 * array stays what it says it is: the canonical DATA, whose order carries no
 * meaning.
 */
export const FORM_ACTION_OPTIONS: { value: string; label: string }[] = FORM_ACTION_LABELS.map(
  (value) => ({ value, label: formActionLabel(value) }),
).sort((a, b) => a.label.localeCompare(b.label))

// ─── Product operations ──────────────────────────────────────────────────────

/**
 * The operations behind the "P" icon, and which of them a given form offers.
 *
 * These are SUB-actions of `PRODUCT_ACTION`: the builder's checklist nests them
 * under it, and each is persisted in the same `allowedActions` array as its
 * parent — prefixed, so an operation can never be mistaken for a top-level
 * action by anything iterating that list (`FormActionBar`'s "+" menu filters
 * against `FORM_ACTION_LABELS`, which these are deliberately not in).
 *
 * Storing them in `allowedActions` rather than in a column of their own is what
 * makes this shippable without a migration, and it is honest about what they
 * are: things this form's action bar offers.
 */
export interface ProductOperationOption {
  /** Persisted in `allowedActions`. Never rename — see FORM_ACTION_LABELS. */
  value: string
  label: string
  /**
   * Whether the operation does anything yet. Only `Create new product` has a
   * backend; the rest are one ticket each and render disabled.
   *
   * Deliberately separate from whether a form OFFERS the operation: selection
   * controls what appears in the menu, `ready` controls whether it can be
   * clicked. A form can name Split product today and have it light up when that
   * ships, with no form definition to re-edit.
   */
  ready: boolean
}

export const PRODUCT_OPERATION_OPTIONS: ProductOperationOption[] = [
  { value: 'Product: Create new product', label: 'Create new product', ready: true },
  { value: 'Product: Split product', label: 'Split product', ready: true },
  { value: 'Product: Pool / combine product', label: 'Pool / combine product', ready: true },
  { value: 'Product: Receive product', label: 'Receive product', ready: true },
  { value: 'Product: Update product', label: 'Update product', ready: true },
  { value: 'Product: Store product', label: 'Store product', ready: true },
  // Ship to a partner (z8nrz7eaex): opens the shared ShipProductDialog.
  { value: 'Product: Ship product', label: 'Ship product', ready: true },
]

/** The operation that actually creates a product — what Complete Form asks about. */
export const CREATE_PRODUCT_OPERATION = PRODUCT_OPERATION_OPTIONS[0].value

/** Ship to a partner — shown only to users holding products.ship (z8nrz7eaex). */
export const SHIP_PRODUCT_OPERATION = 'Product: Ship product'

const OPERATION_VALUES = PRODUCT_OPERATION_OPTIONS.map(o => o.value)

/**
 * Which product operations this form's "P" menu should show.
 *
 * Three cases, and the middle one is the whole point of the feature:
 *
 * - the parent action is off → none. There is no "P" icon to open.
 * - operations are recorded → exactly those, in canonical order.
 * - the parent is on with NO operations recorded → all of them.
 *
 * That last case is a form saved before operations could be chosen, and it must
 * keep behaving as it does today: those forms show the whole menu, and reading
 * "nothing recorded" as "nothing offered" would silently empty the menu on every
 * form already configured. New selections can only ever be explicit, because
 * unchecking the last operation unchecks the parent (see `toggleFormAction`).
 */
export function productOperationsFor(
  allowedActions: string[] | undefined,
): ProductOperationOption[] {
  const actions = allowedActions ?? []
  if (!actions.includes(PRODUCT_ACTION)) return []
  const selected = PRODUCT_OPERATION_OPTIONS.filter(o => actions.includes(o.value))
  return selected.length > 0 ? selected : PRODUCT_OPERATION_OPTIONS
}

/** Does this form offer the operation that creates a product? */
export function offersCreateProduct(allowedActions: string[] | undefined): boolean {
  return productOperationsFor(allowedActions).some(o => o.value === CREATE_PRODUCT_OPERATION)
}

/**
 * Check or uncheck one entry of the actions checklist, parent or operation.
 *
 * One function because there are THREE checklists — the Forms list, the form
 * editor's settings panel and FormDefinitionPanel — and the parent/child rules
 * below cannot be re-expressed in each without drifting. A display rename got
 * applied to one of the three once already.
 *
 * The rules:
 *
 * - Turning the parent ON turns every operation on with it, so a form starts out
 *   offering the whole menu, which is what it offered before operations could be
 *   chosen. Narrowing is then a deliberate act.
 * - Turning the parent OFF takes its operations with it, leaving no orphans
 *   behind to reappear if it is switched back on.
 * - Turning an operation ON implies the parent: an operation nothing can reach
 *   is not a selection.
 * - Turning the LAST operation off turns the parent off too. A product action
 *   offering nothing would put a "P" icon on the bar that opens an empty menu.
 */
export function toggleFormAction(allowedActions: string[], value: string): string[] {
  const actions = allowedActions ?? []
  const has = actions.includes(value)
  const without = (list: string[], drop: string[]) => list.filter(a => !drop.includes(a))

  if (value === PRODUCT_ACTION) {
    return has
      ? without(actions, [PRODUCT_ACTION, ...OPERATION_VALUES])
      : [...actions, PRODUCT_ACTION, ...OPERATION_VALUES]
  }

  if (OPERATION_VALUES.includes(value)) {
    if (!has) {
      const next = actions.includes(PRODUCT_ACTION) ? actions : [...actions, PRODUCT_ACTION]
      return [...next, value]
    }
    const next = without(actions, [value])
    const remaining = OPERATION_VALUES.filter(v => next.includes(v))
    return remaining.length > 0 ? next : without(next, [PRODUCT_ACTION])
  }

  return has ? without(actions, [value]) : [...actions, value]
}
